/* ============================================================
   Път към здраве — theme behaviour
   AJAX cart + drawer, quantity steppers, mobile menu, sticky header.
   Vanilla JS, no dependencies. Respects prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Overlays give focus back (audit P3) ---------- */
  // Closing the cart, the search or the menu used to leave keyboard and screen-reader users
  // at the top of the page. Each overlay remembers what opened it and returns focus there.
  // Safari does not focus a button when it is tapped, so "what had focus" can be the page
  // itself; then the overlay's own toggle button is where focus goes back to.
  const openers = new Map();
  const toggles = { drawer: '[data-cart-toggle]', menu: '[data-menu-toggle]', search: '[data-search-toggle]' };
  function rememberOpener(name) {
    const el = document.activeElement;
    const usable = el && el !== document.body && !el.closest('[data-mobile-nav], .search-modal, [data-cart-drawer]');
    openers.set(name, usable ? el : $(toggles[name]));
  }
  function returnFocus(name) {
    const el = openers.get(name);
    openers.delete(name);
    if (el && document.contains(el)) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }
  }

  /* ---------- Cart drawer ---------- */
  const drawer = () => $('[data-cart-drawer]');

  function openDrawer() {
    const d = drawer();
    if (!d) return;
    if (!d.hasAttribute('open')) rememberOpener('drawer');
    d.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    const close = $('[data-drawer-close]', d);
    if (close) close.focus();
  }
  function closeDrawer() {
    const d = drawer();
    if (!d || !d.hasAttribute('open')) return;
    d.removeAttribute('open');
    document.documentElement.style.overflow = '';
    returnFocus('drawer');
  }

  /* ---------- Mobile menu ---------- */
  const mobileNav = () => $('[data-mobile-nav]');
  function openMenu() {
    const nav = mobileNav();
    if (!nav) return;
    rememberOpener('menu');
    nav.setAttribute('open', '');
    $$('[data-menu-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.documentElement.style.overflow = 'hidden';
    const close = $('[data-mobile-close].icon-btn', nav);
    if (close) setTimeout(() => close.focus(), 60);
  }
  function closeMenu() {
    const nav = mobileNav();
    if (!nav || !nav.hasAttribute('open')) return;
    nav.removeAttribute('open');
    $$('[data-menu-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.documentElement.style.overflow = '';
    returnFocus('menu');
  }

  async function refreshDrawer() {
    try {
      const res = await fetch(`${root}?section_id=cart-drawer`);
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer]');
      const current = drawer();
      if (fresh && current) {
        // The fetched drawer renders closed — carry over the open state so
        // quantity changes inside the drawer don't make it vanish.
        if (current.hasAttribute('open')) fresh.setAttribute('open', '');
        current.replaceWith(fresh);
      }
    } catch (e) { /* drawer refresh is non-critical */ }
  }

  async function refreshCount() {
    try {
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      $$('[data-cart-count]').forEach((el) => {
        // From 100 up the badge says "99+"; data-big lets the CSS size it
        el.textContent = cart.item_count > 99 ? '99+' : cart.item_count;
        el.toggleAttribute('data-big', cart.item_count > 99);
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* noop */ }
  }

  /* ---------- Stock limits (audit A3) ---------- */
  // A variant that is tracked and may not oversell carries its stock as data-max on the
  // quantity input. Shopify's add endpoint does NOT enforce it — with 1 in stock and 1 in the
  // cart it happily made it 2 — and the cart endpoint answers 422 with a ready Bulgarian
  // sentence that the theme used to throw away. Both are handled here.
  const T = window.PKZ_T || {};
  const tn = (key, n) => String(T[key] || '').replace('%n', n);

  function showNote(box, msg) {
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  }
  function noteBoxFor(el) {
    const scope = el && (el.closest('[data-cart-form]') || el.closest('[data-cart-drawer]') || el.closest('[data-cart-page]'));
    return scope ? $('[data-cart-error]', scope) : null;
  }
  async function inCart(variantId) {
    const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    return cart.items
      .filter((i) => String(i.variant_id) === String(variantId))
      .reduce((sum, i) => sum + i.quantity, 0);
  }
  // Say it where it will be seen: the drawer opens with the cart as it really is and the
  // message on top; the product page also keeps it under its own button.
  async function tell(form, msg) {
    showNote($('[data-cart-error]', form), msg);
    await Promise.all([refreshDrawer(), refreshCount()]);
    openDrawer();
    showNote($('[data-cart-error]', drawer()), msg);
  }

  /* ---------- AJAX add to cart ---------- */
  async function addToCart(form) {
    const btn = form.querySelector('[type="submit"]');
    const qtyEl = form.querySelector('[name="quantity"]');
    const idEl = form.querySelector('[name="id"]');
    const cap = parseInt(qtyEl && qtyEl.dataset.max, 10);
    let qty = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
    let note = '';
    showNote($('[data-cart-error]', form), '');
    if (btn) btn.classList.add('is-loading');
    try {
      if (!isNaN(cap) && idEl) {
        const room = cap - await inCart(idEl.value);
        if (room <= 0) { await tell(form, tn('allInCart', cap)); return; }
        if (qty > room) { qty = room; note = tn('addedOnly', room); }
      }
      const body = new FormData(form);
      body.set('quantity', qty);
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body,
      });
      if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        await tell(form, data.description || data.message || T.cartError);
        return;
      }
      if (!res.ok) throw new Error('add failed');
      await Promise.all([refreshDrawer(), refreshCount()]);
      openDrawer();
      if (note) showNote($('[data-cart-error]', drawer()), note);
    } catch (e) {
      // Fallback: let the browser submit normally to /cart
      form.submit();
    } finally {
      if (btn) btn.classList.remove('is-loading');
    }
  }

  /* ---------- Quantity steppers ---------- */
  function stepQty(wrapper, delta) {
    const input = $('input', wrapper);
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    const max = parseInt(input.dataset.max, 10);
    const before = parseInt(input.value || '1', 10);
    let val = before + delta;
    if (isNaN(val) || val < min) val = min;
    if (!isNaN(max) && val > max) {
      val = Math.max(min, max);
      showNote(noteBoxFor(wrapper), tn('maxReached', max));
      if (val === before) return;
    }
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------- Cart line updates (cart page + drawer) ---------- */
  async function changeLine(line, quantity, note) {
    const res = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    });
    let msg = note || '';
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      msg = data.description || data.message || T.cartError || '';
    }
    // Re-render whatever the answer: on a 422 Shopify has still changed the cart — it cut the
    // line to what is in stock — and the page showed the quantity that was asked for, with
    // the wrong total and the wrong free-delivery figure (audit A3).
    await Promise.all([refreshCartPage(), refreshDrawer(), refreshCount()]);
    if (msg) {
      showNote($('[data-cart-page] [data-cart-error]'), msg);
      showNote($('[data-cart-drawer] [data-cart-error]'), msg);
    }
  }

  async function refreshCartPage() {
    const main = $('[data-cart-page]');
    if (!main) return;
    try {
      // JSON-template sections need their full dynamic ID (template--…__main),
      // exposed on the section root as data-section-id.
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(main.dataset.sectionId)}`);
      const html = await res.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-page]');
      if (fresh) main.replaceWith(fresh);
    } catch (e) { window.location.reload(); }
  }

  /* ---------- "Покажи още" — collection and search pages, phones ---------- */
  // Fetches the next page and appends its cards to [data-load-grid]. Lived inline in
  // collection.liquid, so the search page — whose numbered pagination the same phone CSS
  // hides — had no way past its first 24 results (audit A2).
  //
  // Back keeps your place (audit B5). The URL used to become ?page=N, so opening a product and
  // pressing Back reloaded only page N, from the top. Now the URL stays as it was, the number
  // of extra pages and the scroll position are kept for this tab in sessionStorage, and a
  // back/forward visit loads those pages again and scrolls to where the visitor was.
  const moreKey = () => 'pkz:more:' + location.pathname + location.search;
  function readMore() {
    try { return JSON.parse(sessionStorage.getItem(moreKey()) || 'null'); } catch (e) { return null; }
  }
  function writeMore(v) {
    try { if (v) sessionStorage.setItem(moreKey(), JSON.stringify(v)); else sessionStorage.removeItem(moreKey()); } catch (e) { /* noop */ }
  }

  async function loadMore(btn, restoring) {
    const next = btn.getAttribute('data-next');
    if (!next || btn.disabled) return false;
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = '…';
    try {
      const res = await fetch(next);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const grid = $('[data-load-grid]');
      const fresh = $('[data-load-grid]', doc);
      if (grid && fresh) while (fresh.firstElementChild) grid.appendChild(fresh.firstElementChild);
      const nextBtn = $('[data-load-more]', doc);
      if (nextBtn && nextBtn.getAttribute('data-next')) {
        btn.setAttribute('data-next', nextBtn.getAttribute('data-next'));
        btn.disabled = false;
        btn.textContent = label;
      } else {
        // Hide rather than remove: the element keeps the "page links are replaced" CSS in
        // force, so page-1 links do not pop up under the last of the results.
        btn.closest('.load-more').style.display = 'none';
      }
      if (window.PKZWishlist) window.PKZWishlist.paint();
      if (!restoring) {
        const m = readMore() || { n: 0, y: 0 };
        m.n += 1;
        writeMore(m);
      }
      return true;
    } catch (e) {
      btn.disabled = false;
      btn.textContent = label;
      return false;
    }
  }

  window.addEventListener('pagehide', () => {
    const m = readMore();
    if (m && m.n) { m.y = window.scrollY; writeMore(m); }
  });

  (async function restoreMore() {
    const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (!nav || nav.type !== 'back_forward') { writeMore(null); return; }   // a fresh visit starts at page 1
    const m = readMore();
    if (!m || !m.n) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    for (let i = 0; i < m.n; i++) {
      const btn = $('[data-load-more]');
      if (!btn || btn.closest('.load-more').style.display === 'none') break;
      if (!(await loadMore(btn, true))) break;
    }
    window.scrollTo(0, m.y || 0);
  })();

  // The collection page swaps its results in place when filters change (B6); the loaded-pages
  // count belongs to the old results, so the new URL starts clean.
  document.addEventListener('pkz:results-swapped', () => writeMore(null));

  /* ---------- Account popover ---------- */
  function closeAccount() {
    const acct = $('[data-account]');
    if (!acct) return;
    acct.removeAttribute('data-open');
    const t = $('[data-account-toggle]');
    if (t) t.setAttribute('aria-expanded', 'false');
  }

  /* ---------- Predictive search ---------- */
  const searchPanel = () => $('[data-search-panel]');
  let searchTimer;

  function openSearch() {
    const p = searchPanel();
    if (!p) return;
    if (!p.hasAttribute('open')) rememberOpener('search');
    p.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';   // the page no longer scrolls behind it
    const toggle = $('[data-search-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    renderRecent();
    const input = $('[data-search-input]', p);
    if (input) {
      // the long placeholder does not fit a 360px phone — swap it for the short one
      const short = input.dataset.placeholderShort;
      const long = input.dataset.placeholderLong;
      if (short && long) input.placeholder = window.innerWidth <= 560 ? short : long;
      setTimeout(() => input.focus(), 60);
    }
  }
  function closeSearch() {
    const p = searchPanel();
    if (!p || !p.hasAttribute('open')) return;
    p.removeAttribute('open');
    document.documentElement.style.overflow = '';
    p.removeAttribute('data-searching');
    const toggle = $('[data-search-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    const results = $('[data-search-results]', p);
    if (results) results.innerHTML = '';
    const input = $('[data-search-input]', p);
    if (input) input.value = '';
    returnFocus('search');
  }
  function escHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : s;
    return d.innerHTML;
  }
  // escHtml leaves quotes intact, which is fine between tags but not inside an
  // attribute — a search term containing a quote would otherwise break out of it.
  function escAttr(s) {
    return escHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // Follows the shop's own money format (Settings → General → currency formatting), so the
  // dropdown prints prices exactly like the rest of the store and like checkout. It used to
  // hard-code "€" in front (audit B3).
  function fmtPrice(p) {
    if (p == null || p === '') return '';
    const n = Number(p);
    if (isNaN(n)) return String(p);
    const fmt = T.moneyFormat || '€{{amount_with_comma_separator}}';
    const group = (int, sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      const [int, dec] = n.toFixed(2).split('.');
      switch (key) {
        case 'amount_with_comma_separator': return group(int, '.') + ',' + dec;
        case 'amount_no_decimals_with_comma_separator': return group(String(Math.round(n)), '.');
        case 'amount_with_apostrophe_separator': return group(int, "'") + '.' + dec;
        case 'amount_no_decimals': return group(String(Math.round(n)), ',');
        default: return group(int, ',') + '.' + dec;
      }
    });
  }
  // Vendor list is embedded in the search modal; parse it once and reuse.
  let vendorCache = null;
  function allVendors() {
    if (vendorCache) return vendorCache;
    const el = $('[data-search-vendors]');
    if (!el) return (vendorCache = []);
    try { vendorCache = JSON.parse(el.textContent) || []; }
    catch (e) { vendorCache = []; }
    return vendorCache;
  }
  // Substring match, accent/case-insensitive, so a partial like "виол"
  // finds "Виолайф" even mid-word.
  function norm(s) {
    return (s == null ? '' : String(s)).toLocaleLowerCase('bg');
  }
  /* ---------- Recent searches (this device only) ---------- */
  // Stored in localStorage, newest first. Deliberately device-local: Shopify
  // gives themes no writable customer storage from the storefront, so there is
  // nothing to sync to an account without a private app.
  const RECENT_KEY = 'pkz:recent-searches';
  const RECENT_MAX = 6;

  function readRecent() {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string') : [];
    } catch (e) { return []; }   // private mode / storage disabled
  }
  function writeRecent(list) {
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list)); }
    catch (e) { /* storage full or blocked — recents are non-critical */ }
  }
  function addRecent(q) {
    const term = (q == null ? '' : String(q)).trim();
    if (term.length < 2) return;
    // Case-insensitive de-dupe, but keep the shopper's own capitalisation.
    const rest = readRecent().filter((s) => norm(s) !== norm(term));
    writeRecent([term].concat(rest).slice(0, RECENT_MAX));
  }
  function removeRecent(term) {
    writeRecent(readRecent().filter((s) => norm(s) !== norm(term)));
  }
  function renderRecent() {
    $$('[data-search-recent]').forEach((box) => {
      const list = readRecent();
      if (!list.length) { box.hidden = true; box.innerHTML = ''; return; }
      const labelRecent = box.dataset.labelRecent || 'Recent searches';
      const labelClear = box.dataset.labelClear || 'Clear';
      const removeTpl = box.dataset.labelRemove || 'Remove %s';
      const items = list.map((term) => {
        const href = `${root}search?q=${encodeURIComponent(term)}`;
        const aria = escAttr(removeTpl.replace('%s', term));
        return `<span class="search-recent__item">`
          + `<a class="search-recent__term" href="${href}">${escHtml(term)}</a>`
          + `<button type="button" class="search-recent__x" data-recent-remove="${escAttr(term)}"`
          + ` aria-label="${aria}">&times;</button></span>`;
      }).join('');
      box.innerHTML = `<div class="search-recent__head">`
        + `<p class="search-recent__title">${labelRecent}</p>`
        + `<button type="button" class="search-recent__clear" data-recent-clear>${labelClear}</button>`
        + `</div><div class="search-recent__list">${items}</div>`;
      box.hidden = false;
    });
  }

  // Category list (departments + subcategories) is embedded in the search modal too.
  let catCache = null;
  function allCategories() {
    if (catCache) return catCache;
    const el = $('[data-search-categories]');
    if (!el) return (catCache = []);
    try { catCache = JSON.parse(el.textContent) || []; }
    catch (e) { catCache = []; }
    return catCache;
  }
  function matchList(list, q, limit) {
    const nq = norm(q);
    if (!nq) return [];
    const starts = [], contains = [];
    list.forEach((v) => {
      const nv = norm(v.name);
      if (nv.startsWith(nq)) starts.push(v);
      else if (nv.indexOf(nq) !== -1) contains.push(v);
    });
    return starts.concat(contains).slice(0, limit || 6);
  }
  function matchCategories(q, limit) { return matchList(allCategories(), q, limit); }
  function matchVendors(q, limit) {
    const nq = norm(q);
    if (!nq) return [];
    const starts = [], contains = [];
    allVendors().forEach((v) => {
      const nv = norm(v.name);
      if (nv.startsWith(nq)) starts.push(v);
      else if (nv.indexOf(nq) !== -1) contains.push(v);
    });
    return starts.concat(contains).slice(0, limit || 6);
  }
  function renderPredictive(products, vendors, q, results, categories) {
    const labelNone = results.dataset.labelNone || 'No results';
    const labelAll = results.dataset.labelAll || 'View all results';
    const labelBrands = results.dataset.labelBrands || 'Brands';
    const labelProducts = results.dataset.labelProducts || 'Products';
    const labelCats = results.dataset.labelCategories || 'Categories';
    const cats = categories || [];
    if (!products.length && !vendors.length && !cats.length) {
      return `<p class="ps__empty">${labelNone} — “${escHtml(q)}”</p>`;
    }
    let html = '';
    if (cats.length) {
      const chips = cats.map((c) =>
        `<a class="ps__brand" href="${c.url}">${escHtml(c.name)}</a>`).join('');
      html += `<p class="ps__group">${labelCats}</p>`
        + `<div class="ps__brands">${chips}</div>`;
    }
    if (vendors.length) {
      const chips = vendors.map((v) =>
        `<a class="ps__brand" href="${v.url}">${escHtml(v.name)}</a>`).join('');
      html += `<p class="ps__group">${labelBrands}</p>`
        + `<div class="ps__brands">${chips}</div>`;
    }
    if (products.length) {
      const rows = products.map((p) => {
        const src = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        const media = src ? `<img src="${src}" alt="" loading="lazy">` : '';
        const vendor = p.vendor ? `<span class="ps__vendor">${escHtml(p.vendor)}</span>` : '';
        return `<li><a class="ps__item" href="${p.url}">`
          + `<span class="ps__media">${media}</span>`
          + `<span class="ps__info">${vendor}<span class="ps__title">${escHtml(p.title)}</span>`
          + `<span class="ps__price">${fmtPrice(p.price)}</span></span></a></li>`;
      }).join('');
      if (vendors.length) html += `<p class="ps__group">${labelProducts}</p>`;
      html += `<ul class="ps__list">${rows}</ul>`;
    }
    const all = `${root}search?q=${encodeURIComponent(q)}`;
    return html + `<a class="ps__all" href="${all}">${labelAll} — “${escHtml(q)}”</a>`;
  }
  // A "scope" is any container holding one search input + its results panel —
  // the header modal and the /search page each have their own.
  function resultsFor(input) {
    const scope = input.closest('[data-search-scope]');
    return scope ? scope.querySelector('[data-search-results]') : null;
  }
  async function runPredictive(q, results) {
    if (!results) return;
    if (!q || q.length < 2) { results.innerHTML = ''; return; }
    const vendors = matchVendors(q, 6);
    const categories = matchCategories(q, 4);
    let products = [];
    try {
      const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}`
        + `&resources%5Btype%5D=product&resources%5Blimit%5D=8`
        + `&resources%5Boptions%5D%5Bunavailable_products%5D=last`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      products = (((data.resources || {}).results || {}).products) || [];
    } catch (e) { /* product search is non-critical; brands still render */ }
    results.innerHTML = renderPredictive(products, vendors, q, results, categories);
  }

  /* ---------- Event delegation ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-cart-form]');
    if (form) { e.preventDefault(); addToCart(form); return; }
    // Remember what was searched, for the recents list.
    const searchInput = e.target.querySelector && e.target.querySelector('[data-search-input]');
    if (searchInput) addRecent(searchInput.value);
  });

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-search-input]');
    if (!input) return;
    const q = input.value.trim();
    const results = resultsFor(input);
    const scope = input.closest('[data-search-scope]');
    // The header modal swaps its "popular categories" panel for results; the
    // /search page just shows/hides its dropdown.
    if (scope) scope.toggleAttribute('data-searching', q.length >= 2);
    clearTimeout(searchTimer);
    if (q.length < 2) {
      if (results) results.innerHTML = '';
      renderRecent();   // emptying the box brings the recents panel back
      return;
    }
    searchTimer = setTimeout(() => runPredictive(q, results), 200);
  });

  document.addEventListener('click', (e) => {
    // Recent searches: drop one, or clear the lot
    const dropOne = e.target.closest('[data-recent-remove]');
    if (dropOne) {
      e.preventDefault();
      removeRecent(dropOne.getAttribute('data-recent-remove'));
      renderRecent();
      return;
    }
    if (e.target.closest('[data-recent-clear]')) {
      e.preventDefault();
      writeRecent([]);
      renderRecent();
      return;
    }

    // Account popover: toggle on the button, close on any outside click
    const acctToggle = e.target.closest('[data-account-toggle]');
    if (acctToggle) {
      e.preventDefault();
      const acct = $('[data-account]');
      if (acct) acctToggle.setAttribute('aria-expanded', acct.toggleAttribute('data-open'));
    } else if (!e.target.closest('[data-account-pop]')) {
      closeAccount();
    }

    if (e.target.closest('[data-cart-toggle]')) { e.preventDefault(); refreshDrawer().then(openDrawer); }
    if (e.target.closest('[data-drawer-close]') || e.target.hasAttribute('data-drawer-overlay')) closeDrawer();

    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      const p = searchPanel();
      if (p && p.hasAttribute('open')) closeSearch(); else openSearch();
    }
    if (e.target.closest('[data-search-close]')) closeSearch();

    if (e.target.closest('[data-menu-toggle]')) {
      const nav = mobileNav();
      if (nav && nav.hasAttribute('open')) closeMenu(); else openMenu();
    }
    if (e.target.closest('[data-mobile-close]')) closeMenu();

    const step = e.target.closest('[data-qty-step]');
    if (step) {
      const wrapper = step.closest('.qty');
      stepQty(wrapper, step.dataset.qtyStep === 'up' ? 1 : -1);
    }

    const remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); changeLine(parseInt(remove.dataset.lineRemove, 10), 0); }

    const more = e.target.closest('[data-load-more]');
    if (more) { e.preventDefault(); loadMore(more); }
  });

  // Cart line quantity change + collection facet filters (delegated)
  document.addEventListener('change', (e) => {
    const lineInput = e.target.closest('[data-line]');
    if (lineInput) {
      const line = parseInt(lineInput.dataset.line, 10);
      let qty = Math.max(0, parseInt(lineInput.value || '0', 10));
      const max = parseInt(lineInput.dataset.max, 10);
      let note = '';
      if (!isNaN(max) && qty > max) { qty = max; note = tn('maxReached', max); }
      changeLine(line, qty, note);
      return;
    }
    // The filter panel applies on change as a sidebar, but waits for "Приложи" as a drawer
    const facets = e.target.closest('[data-facets]');
    // requestSubmit so the panel's own submit handler runs (it drops empty price boxes)
    if (facets && !facets.hasAttribute('data-manual-apply')) {
      if (facets.requestSubmit) facets.requestSubmit(); else facets.submit();
    }
  });

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeDrawer(); closeSearch(); closeAccount(); closeMenu(); } });

  /* ---------- Focus trap for open overlays ---------- */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const panel = $('.cart-drawer[open] .cart-drawer__panel')
      || $('.search-modal[open] .search-modal__dialog')
      || $('.mobile-nav[open] .mobile-nav__panel');
    if (!panel) return;
    const focusable = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', panel)
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!panel.contains(document.activeElement)) {
      first.focus(); e.preventDefault();
    } else if (e.shiftKey && document.activeElement === first) {
      last.focus(); e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus(); e.preventDefault();
    }
  });

  /* ---------- Header: hides on the way down, returns on the way up (zelen.bg) ---------- */
  // The section wrapper is the sticky element (header.liquid) — the header alone could not
  // stick, its wrapper being exactly its own height (audit B1). Hidden = the wrapper's top
  // slides up by the header's height; a transform would make the menu and search overlays,
  // which are fixed-position siblings inside that wrapper, size themselves to the header.
  // --header-offset tells other sticky things (product photo, filter sidebar, cart summary)
  // how much of the screen the header is using right now.
  const header = $('[data-site-header]');
  const headerWrap = header && header.closest('.shopify-section');
  if (header && headerWrap) {
    headerWrap.classList.add('site-header-wrap');
    let lastY = window.scrollY;
    let hidden = false;
    const root2 = document.documentElement;
    const apply = () => {
      const h = header.offsetHeight;
      root2.style.setProperty('--header-h', h + 'px');
      root2.style.setProperty('--header-offset', hidden ? '0px' : h + 'px');
      headerWrap.toggleAttribute('data-header-hidden', hidden);
    };
    const onScroll = () => {
      const y = window.scrollY;
      header.toggleAttribute('data-scrolled', y > 8);
      const h = header.offsetHeight;
      const locked = root2.style.overflow === 'hidden';                 // a drawer or menu is open
      const busy = header.contains(document.activeElement) || $('[data-account][data-open]');
      if (y <= h || locked || busy) hidden = false;
      else if (y > lastY + 6) hidden = true;                           // going down
      else if (y < lastY - 6) hidden = false;                          // coming back up
      else { lastY = y; return; }
      lastY = y;
      apply();
    };
    apply();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', apply);
    header.addEventListener('focusin', () => { if (hidden) { hidden = false; apply(); } });
  }

  // Paint any recent searches already stored on this device (e.g. the bare
  // /search page, which shows them without the modal being opened).
  renderRecent();

})();
